// src/pages/memberProfile/Partners.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import i18n from "@/i18n";
import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";
import cls from "./MemberProfileEditPartners.module.scss";

const MemberProfileEditPartners = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const navigate = useNavigate();
  const t = (key) => i18n.t(`memberPartners.${key}`);

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/v1/member/profile/partners");
      setPartners(data?.partners || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleMakePrimary = async (partnerId) => {
    if (!partnerId) return;
    setUpdatingId(partnerId);
    try {
      await api.patch(`/v1/member/profile/partners/${partnerId}/make-primary`);

      // Update local state so UI reflects new primary without refetch
      setPartners((prev) =>
        prev.map((p) => ({
          ...p,
          isPrimary: p.id === partnerId,
        })),
      );
    } catch (err) {
      setError(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const hasPartners = !loading && partners && partners.length > 0;

  const handleBack = () => {
    navigate(-1);
  };
  // Adjust these routes as needed to match your real partner create / detail routes
  const addPartnerHref = "/lounge/my-profile/edit/partners/new";
  const partnerDetailHref = (id) => `/lounge/my-profile/edit/partners/${id}`;

  return (
    <div className={cls.page}>
      {/* Header */}
      <header className={cls.header}>
        <div className={cls.titleRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← {t("back", { defaultValue: "Back" })}
          </button>
        </div>
        <div className={cls.titleRow}>
          <h1 className={cls.title}>{t("title")}</h1>
        </div>
        <p className={cls.subtitle}>{t("subtitle")}</p>

        <div className={cls.actions}>
          <Link to={addPartnerHref} className={cls.btnPrimary}>
            {t("addPartner")}
          </Link>
        </div>
      </header>

      {/* Error message */}
      {error && (
        <div className={cls.errorBox}>
          <p className={cls.errorText}>{t("loadError")}</p>
          <button
            type="button"
            className={cls.btnGhost}
            onClick={fetchPartners}
          >
            {t("reload")}
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className={cls.loaderRow}>
          <div className={cls.skeletonCard} />
          <div className={cls.skeletonCard} />
        </div>
      )}

      {/* Empty state */}
      {!loading && !hasPartners && (
        <section className={cls.empty}>
          <h2 className={cls.emptyTitle}>{t("emptyTitle")}</h2>
          <p className={cls.emptyBody}>{t("emptyBody")}</p>
          <Link to={addPartnerHref} className={cls.btnPrimary}>
            {t("emptyCta")}
          </Link>
        </section>
      )}

      {/* Partners grid */}
      {hasPartners && (
        <section className={cls.grid} aria-label="Partners list">
          {partners.map((partner) => {
            const isPrimary = Boolean(partner.isPrimary);
            const name = partner.firstName || t("unknownName");
            const imageUrl = partner.realImage?.url || null;

            return (
              <article
                key={partner.id}
                className={`${cls.card} ${isPrimary ? cls.cardPrimary : ""}`}
              >
                {/* Left: round image */}
                <div className={cls.imageWrap}>
                  <div className={cls.imageInner}>
                    {imageUrl ? (
                      <img src={imageUrl} alt={name} className={cls.image} />
                    ) : (
                      <div className={cls.imageFallback}>
                        <img
                          src={avatarPng}
                          alt=""
                          className="avatar avatar-md"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: content */}
                <div className={cls.body}>
                  {/* ROW 1 – Name */}
                  <h3 className={cls.name}>{name}</h3>

                  {/* ROW 2 – Badge OR Make Primary */}
                  <div className={cls.rowBadge}>
                    {isPrimary ? (
                      <span className={cls.badgePrimary}>
                        {t("badgePrimary")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={cls.primaryBtnSmall}
                        onClick={() => handleMakePrimary(partner.id)}
                        disabled={updatingId === partner.id}
                      >
                        {updatingId === partner.id
                          ? t("makingPrimary")
                          : t("makePrimary")}
                      </button>
                    )}
                  </div>

                  {/* ROW 3 – View Profile */}
                  <div className={cls.rowView}>
                    <Link
                      to={partnerDetailHref(partner.id)}
                      className={cls.viewBtn}
                    >
                      {t("viewProfile")}
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default MemberProfileEditPartners;
